class SiblingProductSwitcher extends HTMLElement {
  constructor() {
    super();
    this.inputs = [];
    this.sectionId = this.getAttribute('data-section-id');
    this.productCache = new Map();
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    this.inputs = Array.from(this.querySelectorAll('input[name="sibling-product"]'));
    this.attachEvents();
    // Preload siblings when idle
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.preloadSiblings());
    } else {
      setTimeout(() => this.preloadSiblings(), 300);
    }
  }

  disconnectedCallback() {
    this.detachEvents();
  }

  attachEvents() {
    this.inputs.forEach((input) => input.addEventListener('change', this.onChange));
  }

  detachEvents() {
    this.inputs.forEach((input) => input.removeEventListener('change', this.onChange));
  }

  async onChange(event) {
    const target = event.target;

    this.inputs.forEach((input) => {
      input.checked = false;
    });

    target.checked = true;

    if (!target?.value) return;

    const handle = target.value;
    const productId = target.dataset.productId;

    try {
      const newSection = await this.getSectionForHandle(handle);
      if (!newSection) return;

      const newSectionClone = newSection.cloneNode(true);

      const currentSection = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!currentSection) return;

      this.preserveNonReplacable(currentSection, newSectionClone);
      this.preserveKlarnaWidget(currentSection, newSectionClone);
      
      // Add class to elements that require adding a class on fetch
      const classOnFetchElements = newSectionClone.querySelectorAll('[data-class-on-fetch]');
      classOnFetchElements.forEach((element) => {
        element.classList.add(element.dataset.classOnFetch);
      });

      currentSection.replaceWith(newSectionClone);

      // Basic re-init for behaviors that usually run on DOMContentLoaded
      if (window.jQuery && typeof window.jQuery.fn.magnify === 'function') {
        window.jQuery('.zoomHolder').magnify();
      }

      // Update URL (preserve query params unrelated to product)
      const url = new URL(window.location.href);
      url.pathname = `/products/${handle}`;
      url.search = '';
      window.history.replaceState({ path: url.toString() }, '', url.toString());

      // Announce change if anyone needs to react
      window.dispatchEvent(new CustomEvent('url:changed', { detail: { productId } }));
    } catch (e) {
      console.error('[SiblingProductSwitcher] Failed to switch product', e);
    }
  }

  async getSectionForHandle(handle) {
    if (this.productCache.has(handle)) return this.productCache.get(handle);

    const response = await fetch(`/products/${handle}?section_id=${this.sectionId}`);
    const htmlText = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, 'text/html');
    const section = doc.getElementById(`MainProduct-${this.sectionId}`);
    if (section) this.productCache.set(handle, section);
    return section;
  }

  preloadSiblings() {
    this.inputs.forEach((input) => {
      const handle = input.value;
      if (!this.productCache.has(handle)) {
        this.getSectionForHandle(handle).catch(() => { });
      }
    });
  }

  preserveNonReplacable(currentSection, incomingSection) {
    try {
      const currentList = Array.from(
        currentSection.querySelectorAll('[data-sibling-product-switcher-non-replacable]')
      );
      if (!currentList.length) return;

      const incomingList = Array.from(
        incomingSection.querySelectorAll('[data-sibling-product-switcher-non-replacable]')
      );

      const count = Math.min(currentList.length, incomingList.length);
      for (let i = 0; i < count; i++) {
        const keepNode = currentList[i];
        const replaceTarget = incomingList[i];
        if (!keepNode || !replaceTarget) continue;
        replaceTarget.replaceWith(keepNode);
      }
    } catch (err) {
      console.warn('[SiblingProductSwitcher] preserveNonReplacable failed', err);
    }
  }

  preserveKlarnaWidget(currentSection, incomingSection) {
    try {
      const currentKlarnaWidget = currentSection.querySelector('klarna-placement');
      if (!currentKlarnaWidget) return;
      const incomingKlarnaWidget = incomingSection.querySelector('klarna-placement');
      if (!incomingKlarnaWidget) return;
      incomingKlarnaWidget.replaceWith(currentKlarnaWidget);
    } catch (err) {
      console.warn('[SiblingProductSwitcher] preserveKlarnaWidget failed', err);
    }
  }
}

if (!customElements.get('sibling-product-switcher')) {
  customElements.define('sibling-product-switcher', SiblingProductSwitcher);
}